/**
 * How the value of a count or a write-off reads, in the Admin and on the stations alike
 * (UI8B-2, UI8X-3). One rule: a count signs every value ("−R 300.00 · +R 50.00"), because
 * a count can find stock as well as lose it; an unsigned figure is always labelled
 * ("R 350.00 moved"); a write-off is a loss and says so ("−R 301.25 written off").
 * @module
 */
import { cents, formatMoney } from '../money/index.js';

/** Money with its sign always shown: `+R 50.00`, `−R 300.00`; zero has none: `R 0.00`. */
export function formatSignedMoney(amount: number): string {
  const text = formatMoney(cents(amount));
  return amount > 0 ? `+${text}` : text;
}

/** "R 350.00 moved": every difference up or down, so unsigned, and labelled as such. */
export function movedValueText(gross: number): string {
  return `${formatMoney(cents(Math.abs(gross)))} moved`;
}

/**
 * A count's value from its signed values (per row or per product; `null` without a cost):
 * the losses and the gains, each signed, "−R 300.00 · +R 50.00"; one side alone,
 * "−R 300.00" or "+R 50.00"; `R 0.00` when they are all zero. `null` when none has a cost.
 */
export function countValueText(values: readonly (number | null)[]): string | null {
  const costed = values.filter((v): v is number => v !== null);
  if (costed.length === 0) return null;
  const losses = costed.filter((v) => v < 0).reduce((s, v) => s + v, 0);
  const gains = costed.filter((v) => v > 0).reduce((s, v) => s + v, 0);
  const parts = [losses, gains].filter((v) => v !== 0).map(formatSignedMoney);
  return parts.length === 0 ? formatSignedMoney(0) : parts.join(' · ');
}

/** "−R 301.25 written off": a write-off's value (given either way round) is a loss. */
export function writeOffValueText(value: number): string {
  return `${formatSignedMoney(-Math.abs(value))} written off`;
}
